import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authService } from '../services/authService'
import { completeGoogleSignIn, redirectForRole } from '../utils/completeGoogleSignIn'
import { openGoogleOAuthPopup, type GoogleOAuthPopupOptions } from '../utils/googleOAuthPopup'

type GoogleSignInButtonProps = {
  className?: string
  onSuccess?: (role: string) => void
  onError?: (message: string) => void
  /** OAuth intended role — use affiliate on partners / affiliate auth pages. */
  role?: GoogleOAuthPopupOptions['role']
  label?: string
  busyLabel?: string
}

const GoogleSignInButton = ({
  className = '',
  onSuccess,
  onError,
  role,
  label,
  busyLabel,
}: GoogleSignInButtonProps) => {
  const { t } = useTranslation('marketing')
  const [config, setConfig] = useState<{ enabled: boolean; client_id?: string | null } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    authService
      .getGoogleAuthConfig()
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, client_id: null }))
  }, [])

  const handleClick = async () => {
    if (busy) return

    if (!config?.enabled || !config.client_id) {
      const message = t(
        'auth.googleNotConfigured',
        'Google sign-in is not configured yet. Add your Google OAuth client ID and secret in Admin → Platform Settings → Integrations.',
      )
      if (!onError) alert(message)
      onError?.(message)
      return
    }

    setBusy(true)
    try {
      const { access_token } = await openGoogleOAuthPopup({
        role,
        skipReferral: role === 'affiliate',
      })
      const user = await completeGoogleSignIn(access_token)
      if (onSuccess) {
        onSuccess(user.role)
      } else {
        redirectForRole(user.role)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.googleFailed', 'Google sign-in failed.')
      if (!message.includes('cancelled')) {
        if (!onError) alert(message)
      }
      onError?.(message)
    } finally {
      setBusy(false)
    }
  }

  const idleLabel = label || t('auth.continueWithGoogle', 'Continue with Google')
  const workingLabel = busyLabel || t('auth.signingInWithGoogle', 'Signing in…')

  return (
    <button
      type="button"
      className={`google-sign-in-btn ${className}`.trim()}
      onClick={() => void handleClick()}
      disabled={busy}
      aria-busy={busy}
    >
      <svg className="google-sign-in-icon" viewBox="0 0 24 24" aria-hidden>
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      {busy ? workingLabel : idleLabel}
    </button>
  )
}

export default GoogleSignInButton
